import * as React from 'react'
import { getAffiliateIdFromStorage as defaultGetAffiliateIdFromStorage } from '@repo/affiliate-id'
import { usePaymentsDialog } from '../../components/payments-dialog-wrapper-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserEligibleToAutostartQuery } from 'codegen/generated/user'
import { paymentsAutoStartFreeTrialMutationFetcher } from '../../gen/stripe'
import * as Sentry from '@sentry/react'
import { SentryFilterTagAutostartFreeTrial } from '@repo/sentry-utils/constants'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { mapCurrencyToAvailableCurrencyCode } from '@repo/cf-user/hooks/use-user-data'
import { SubscriptionTypeAutoStartTrial } from 'codegen/types'
import { readPaymentModalEntryPoint } from '../../payments-dialog/analytics'

type SubscriptionType = 'all-access' | 'studio-ai'

export interface StartFreeTrialProps {
  redirectUrl?: string
  openInNewTab?: boolean
  subscriptionType?: SubscriptionType
  promoCode?: string
  // Injected so tests can supply a fake without module mocks; production uses
  // the real localStorage-backed reader from `@repo/affiliate-id`.
  getAffiliateIdFromStorage?: () => string | null
}

let autoStartFreeTrialAttempted = false

// oxlint-disable-next-line complexity
export async function startFreeTrial({
  onSuccess,
  onError,
  subscriptionType = 'all-access',
  promoCode,
  getAffiliateIdFromStorage = defaultGetAffiliateIdFromStorage,
}: {
  subscriptionType?: SubscriptionType
  onSuccess?: () => void
  onError?: () => void
  promoCode?: string
  getAffiliateIdFromStorage?: () => string | null
}) {
  if (autoStartFreeTrialAttempted) {
    return
  }

  let userData = null
  autoStartFreeTrialAttempted = true
  try {
    //This is only needed here, no need to fetch it with each page load
    userData = await useUserEligibleToAutostartQuery.fetcher()()
  } catch (error) {
    Sentry.captureException(
      new Error(
        `useUserEligibleToAutostartQuery fetcher failed with error: ${
          (error as Error)?.message ? (error as Error)?.message : (error as string)
        }`,
      ),
      {
        tags: {
          component: SentryFilterTagAutostartFreeTrial,
        },
      },
    )
  }

  const isEligibleForAutoStartTrial =
    userData && subscriptionType === 'all-access'
      ? userData?.me?.user?.subscriptionAccess.allAccess.isEligibleForAutoStartTrial
      : userData?.me?.user?.subscriptionAccess.studioAi.isEligibleForAutoStartTrial

  const entryPoint = readPaymentModalEntryPoint()

  //If user is eligible to autostart the free trial, and has a stripe customer id,
  //Try to autostart the free trial
  if (isEligibleForAutoStartTrial && userData?.me?.user?.stripeCustomer?.id) {
    //If the feature flag is enabled, try to autostart the free trial
    const currencyCode = userData.me.user.currency.code
    sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_START_CLICKED, {
      subscription_type: subscriptionType,
      entry_point: entryPoint,
      promoCode: promoCode || 'none',
    })
    try {
      const result = await paymentsAutoStartFreeTrialMutationFetcher({
        stripeCustomerId: userData.me.user.stripeCustomerId,
        currencyCode: mapCurrencyToAvailableCurrencyCode(currencyCode),
        promoCode: promoCode,
        subscriptionType:
          subscriptionType === 'all-access'
            ? SubscriptionTypeAutoStartTrial.AllAccessYearly
            : SubscriptionTypeAutoStartTrial.StudioAiYearlyV2,
        // Forward the captured ref for apps that can't share the affwp_ref cookie
        // cross-domain. On cookie-bearing domains BE reads the cookie directly.
        affiliateId: getAffiliateIdFromStorage() ?? undefined,
      })

      if (
        result.paymentsAutoStartTrial.success &&
        result.paymentsAutoStartTrial.stripeSubscriptionId
      ) {
        sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_SUCCESS, {
          subscription_type: subscriptionType,
          entry_point: entryPoint,
          promoCode: promoCode || 'none',
        })
        onSuccess?.()

        //Show confirmation dialog
        const urlSearchParams = new URLSearchParams()
        urlSearchParams.set('is_free_trial_autostart_success', 'true')
        urlSearchParams.set('cf_user_id', userData.me.user.id)
        urlSearchParams.set('cf_currency', String(currencyCode))
        urlSearchParams.set(
          'cf_subscription_type',
          subscriptionType === 'all-access'
            ? 'allAccessYearlyFreeTrial'
            : 'studioAiYearlyFreeTrial',
        )
        urlSearchParams.set(
          'cf_subscription_id',
          result.paymentsAutoStartTrial.stripeSubscriptionId,
        )
        urlSearchParams.set('cf_stripe_account', userData.me.user.stripeCustomer?.account)
        autoStartFreeTrialAttempted = false

        //Using a timeout to ensure that any ongoing analytics or state updates are completed before redirecting
        setTimeout(() => {
          const redirectUrl = new URL(window.location.href)
          redirectUrl.hash = ''
          for (const [key, value] of urlSearchParams) {
            redirectUrl.searchParams.set(key, value)
          }
          window.location.href = redirectUrl.toString()
        }, 500)

        return
      }

      // oxlint-disable-next-line no-negated-condition
      if (!result.paymentsAutoStartTrial.stripeSubscriptionId) {
        Sentry.captureException(
          new Error(
            `paymentsAutoStartFreeTrial mutation did not return a stripe subscription id: ${result.paymentsAutoStartTrial.errors?.join(', ')}`,
          ),
          {
            tags: {
              component: SentryFilterTagAutostartFreeTrial,
            },
          },
        )
      } else {
        Sentry.captureException(
          new Error(
            `paymentsAutoStartFreeTrial mutation failed with message: status: ${result.paymentsAutoStartTrial.success}, errors: ${result.paymentsAutoStartTrial.errors?.join(', ')}`,
          ),
          {
            tags: {
              component: SentryFilterTagAutostartFreeTrial,
            },
          },
        )
      }
    } catch (error) {
      //Log to Sentry
      Sentry.captureException(
        new Error(
          `paymentsAutoStartFreeTrial mutation failed with error: ${
            (error as Error)?.message ? (error as Error)?.message : (error as string)
          }`,
        ),
        {
          tags: {
            component: SentryFilterTagAutostartFreeTrial,
          },
        },
      )
    }
  }

  sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_FAILED, {
    subscription_type: subscriptionType,
    entry_point: entryPoint,
    promoCode: promoCode || 'none',
  })

  onError?.()
  autoStartFreeTrialAttempted = false
}

export function useStartFreeTrial({
  subscriptionType = 'all-access',
  redirectUrl,
  openInNewTab = false,
  promoCode,
  getAffiliateIdFromStorage = defaultGetAffiliateIdFromStorage,
}: StartFreeTrialProps = {}) {
  const [isPending, setIsPending] = React.useState(false)
  const { open } = usePaymentsDialog()
  const { data: isUserLoggedIn } = useIsLoggedInQuery()

  const defaultPlan = subscriptionType === 'all-access' ? 'freeTrialYearly' : 'aiStudio'

  let redirectUrlFinal =
    subscriptionType === 'all-access' ? '/b/free-trial/' : '/b/free-trial-aistudio/'

  if (redirectUrl) {
    redirectUrlFinal = redirectUrl
  }

  const handleStartFreeTrial = async () => {
    if (isPending) {
      return
    }
    //if user is not logged in, redirect
    //Using window.location.href here to make the hook re-usable outside of Next.js (WP, Studio, etc)
    if (!isUserLoggedIn) {
      if (redirectUrlFinal.length === 0) {
        return
      }
      if (openInNewTab) {
        window.open(redirectUrlFinal, '_blank')
        return
      }

      window.location.href = redirectUrlFinal
      return
    }

    setIsPending(true)
    await startFreeTrial({
      subscriptionType,
      promoCode,
      getAffiliateIdFromStorage,
      onSuccess: () => {
        setIsPending(false)
      },
      onError: () => {
        setIsPending(false)

        //Fallback to opening the dialog
        if (window.cfOpenPaymentsDialog) {
          window?.cfOpenPaymentsDialog({
            defaultPlan,
          })
        } else {
          open(defaultPlan)
        }
      },
    })
  }

  return { startFreeTrial: handleStartFreeTrial, isPending }
}
